import React, { useState } from 'react';
import { useDispatch } from "react-redux";

import { Box, Card, Divider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SettingsIcon from '@mui/icons-material/Settings';

// --- Cost Value Driver Components ---
import CostValueDriverLabor from './CostValueDriverLabor';
import CostValueDriverCS from './CostValueDriverCS';
import CostValueDriverOperating from './CostValueDriverOperating';
import CostValueDriverRandD from './CostValueDriverRandD';
import CostValueDriverInvestment from './CostValueDriverInvestment';
import CostValueDriverCompliance from './CostValueDriverCompliance';
import CostValueDriverInventory from './CostValueDriverInventory';
import CostValueDriverFacility from './CostValueDriverFacility';
import CostValueDriverEquipment from './CostValueDriverEquipment';
import CostValueDriverUpgrade from './CostValueDriverUpgrade';

// --- Revenue Value Driver Components ---
import RevenueValueDriverSales from './RevenueValueDriverSales';
import RevenueValueDriverMarketing from './RevenueValueDriverMarketing';
import RevenueValueDriverTTM from './RevenueValueDriverTTM';
import RevenueValueDriverIR from './RevenueValueDriverIR';
import RevenueValueDriverCLV from './RevenueValueDriverCLV';

// --- Risk Value Driver Components ---
import RiskValueDriverRegulatory from './RiskValueDriverRegulatory';
import RiskValueDriverLitigation from './RiskValueDriverLitigation';
import RiskValueDriverRevenue from './RiskValueDriverRevenue';

// import { getIconFromDriverName } from './iconFunctions';
// import { formatString } from './valueFunctions';

import { addDriver } from '../../actions/valueActions';
import OutcomeDriverList from './OutcomeDriverList';
import OutcomeDriverSingle from './OutcomeDriverSingle';

const metricsDrivers = [
    'revenue_loss',
    'litigation_risk',
    'regulatory_risk',
    'sales-driven_revenue',
    'customer_lifetime_value',
    'marketing-driven_revenue',
    'incremental_revenue',
    'time_to_market',
    'facilities_and_infrastructure',
    'production_lines_and_equipment',
    'tools_and_equipment',
    'customer_support',
    'inventory',
]

const ValueDriverDisplay = ({ driver, costStructure, id, valueModel, outcome, outcome_explanation, reason, affectedTypes, driver_analysis }) => {
    const dispatch = useDispatch();
    const [view, setView] = React.useState('ai');

    const handleChange = (event, nextView) => {
        setView(nextView);
    };

    const renderViewToggle = () => {
        return (
            <ToggleButtonGroup
                color="primary"
                value={view}
                exclusive
                onChange={handleChange}
                size='small'
            >
                <ToggleButton value="ai">
                    <SmartToyOutlinedIcon />
                </ToggleButton>
                <ToggleButton value="manual">
                    <SettingsIcon />
                </ToggleButton>
            </ToggleButtonGroup>
        )
    }

    // configuration for all operational-like drivers
    const driverConfig = {
        operational_costs: { driverPath: 'operating_savings', type: 'operating', name: 'Operational Costs' },
        finance_and_accounting: { driverPath: 'accounting_savings', type: 'accounting', name: 'Finance and Accounting' },
        HR_and_people: { driverPath: 'hr_savings', type: 'hr', name: 'HR and People' },
        sales_and_business_development: { driverPath: 'sales_savings', type: 'bd', name: 'Sales and Business Development' },
        marketing_and_communications: { driverPath: 'marketing_savings', type: 'marcom', name: 'Marketing and Communications' },
        customer_success: { driverPath: 'customer_success_savings', type: 'customer_success', name: 'Customer Success' },
        IT_and_infrastructure: { driverPath: 'it_savings', type: 'it', name: 'IT and Infrastructure' },
        legal_and_compliance: { driverPath: 'legal_savings', type: 'legal', name: 'Legal and Compliance' },
        procurement_and_purchasing: { driverPath: 'procurement_savings', type: 'procurement', name: 'Procurement and Purchasing' },
    };

    const isOperational = Object.prototype.hasOwnProperty.call(driverConfig, driver);

    let Component = null;
    let propsToPass = { driver, outcome };
    let costs = null;

    // const createCostTitle = (driver) => {
    //     return (
    //         <Stack direction="row" spacing={1} sx={{ mt: 3, ml: 2, mb: 2, alignItems: 'center' }}>
    //             <Box component={getIconFromDriverName(driver)} />
    //             <Typography variant="body" sx={{ fontWeight: 'bold' }}>
    //                 {formatString(driver)}
    //             </Typography>
    //         </Stack>
    //     )
    // }

    // First, attempt to locate cost information (if applicable).
    if (costStructure?.cost_structure && costStructure.cost_structure[driver]) {
        costs = costStructure.cost_structure[driver];
    } else if (costStructure?.investment_structure && costStructure.investment_structure[driver]) {
        costs = costStructure.investment_structure[driver];
    } else if (driver === 'production_lines_and_equipment' || driver === 'tools_and_equipment') {
        costs = costStructure.investment_structure.production_lines_and_equipment || costStructure.investment_structure.tools_and_equipment
    }

    // Map cost-related drivers.
    if (driver === 'personnel_costs') {
        Component = CostValueDriverLabor;
    } else if (driver === 'customer_support') {
        Component = CostValueDriverCS;
    } else if (isOperational) {
        Component = CostValueDriverOperating;
        propsToPass.filterCosts = ['warehouse_storage'];
    } else if (driver === 'research_and_development') {
        Component = CostValueDriverRandD;
    } else if (driver === 'capital_investment') {
        Component = CostValueDriverInvestment;
    } else if (driver === 'compliance_costs') {
        Component = CostValueDriverCompliance;
    } else if (driver === 'inventory') {
        Component = CostValueDriverInventory;
        propsToPass = {
            ...propsToPass,
            productionCosts: costStructure?.costs_sum?.production_costs,
            warehouseCosts:
                costStructure?.cost_structure?.operational_costs?.warehouse_storage,
            driver: 'inventory_costs',
            cost: 'warehouse_storage',
        };
    } else if (driver === 'facilities_and_infrastructure') {
        Component = CostValueDriverFacility;
    } else if (driver === 'production_lines_and_equipment' || driver === 'tools_and_equipment') {
        Component = CostValueDriverEquipment;
    } else if (driver === 'upgrades') {
        Component = CostValueDriverUpgrade;
    }
    // Map revenue-related drivers.
    else if (driver === 'sales-driven_revenue') {
        Component = RevenueValueDriverSales;
    } else if (driver === 'marketing-driven_revenue') {
        Component = RevenueValueDriverMarketing;
    } else if (driver === 'time_to_market') {
        Component = RevenueValueDriverTTM;
        propsToPass.products = costStructure?.product_types;
    } else if (driver === 'incremental_revenue') {
        Component = RevenueValueDriverIR;
        propsToPass.revenueTypes = costStructure?.revenue_types;
    } else if (driver === 'customer_lifetime_value') {
        Component = RevenueValueDriverCLV;
    }
    // Map risk-related drivers.
    else if (driver === 'regulatory_fines/penalties' || driver === 'regulatory_risk') {
        Component = RiskValueDriverRegulatory;
        propsToPass.risks = costStructure?.risk_structure?.regulatory_fines;
    } else if (driver === 'litigation_risks' || driver === 'litigation_risk') {
        Component = RiskValueDriverLitigation;
        propsToPass.risks = costStructure?.risk_structure?.litigation_risk;
    } else if (driver === 'revenue_loss') {
        Component = RiskValueDriverRevenue;
    } else {
        return null;
    }

    // For cost and risk drivers, pass costs/risks if they exist.
    if (costs) {
        propsToPass.costs = costs;
    }

    const onSubmitDriver = async (type, values, { setSubmitting, setErrors }) => {
        let success = false;
        let result = {};

        if (type) {
            result = await dispatch(addDriver(type, id, values, { setSubmitting, setErrors }))
        } else {
            result = await dispatch(addDriver(values.cost_type, id, values, { setSubmitting, setErrors }))
        }
        success = result?.success;

        // if (formikRef.current && success) {
        //     formikRef.current.resetForm();
        // }
    }

    return (
        <Box key={driver}>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Box
                sx={{
                    mb: 5,
                    width: '100%',
                    maxWidth: 800,
                    display: 'flex',
                    justifyContent: 'center',// center children horizontally
                    alignItems: 'center'     // (optional) center vertically if it has a height
                }}
            >
                {renderViewToggle()}
            </Box>

            {(driver === 'personnel_costs' || isOperational) && view === 'ai' ?
                <React.Fragment>
                    <OutcomeDriverList
                        valueModel={valueModel}
                        driver_analysis={driver_analysis}
                        selectedOutcome={outcome}
                        selectedOutcomeExplanation={outcome_explanation}
                        selectedDriver={driver}
                        driverConfig={driverConfig}
                        onAddDriver={(values, { setSubmitting, setErrors }) => onSubmitDriver(null, values, { setSubmitting, setErrors })}
                    />
                </React.Fragment>
                : metricsDrivers.includes(driver) && view === 'ai' ?
                    <React.Fragment>
                        <OutcomeDriverSingle
                            valueModel={valueModel}
                            driver_analysis={driver_analysis}
                            selectedOutcome={outcome}
                            selectedOutcomeExplanation={outcome_explanation}
                            selectedDriver={driver}
                            selectedDriverReason={reason}
                            driverConfig={driverConfig}
                            affectedTypes={affectedTypes}
                            onAddDriver={(type, values, { setSubmitting, setErrors }) => onSubmitDriver(type, values, { setSubmitting, setErrors })}
                        />
                    </React.Fragment>
                    : <Box sx={{ maxWidth: 800 }}><Component id={id} valueModel={valueModel} {...propsToPass} /></Box>
            }

        </Box>
    );
};

export default ValueDriverDisplay;
